/**
 * Flat SVG icons — the platform standard. Icons are simple geometric
 * shapes rendered in currentColor: no emoji, no platform-styled glyphs.
 * Add new icons here so every surface shares the same visual language.
 * Every navigation destination gets its own unique icon.
 */

interface IconProps {
  size?: number;
  className?: string;
}

function base(size: number, className: string | undefined, label: string) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    role: 'img' as const,
    'aria-label': label,
  };
}

/* ---------------- Voice ---------------- */

/** Microphone — voice input. */
export function MicIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Microphone')}>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <line x1="12" y1="17" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

/** Speaker — spoken replies. */
export function SpeakerIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Speaker')}>
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M18.5 5.5a9 9 0 0 1 0 13" />
    </svg>
  );
}

/** Speaker with a slash — spoken replies off. */
export function SpeakerOffIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Speaker off')}>
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      <line x1="22" y1="9" x2="16" y2="15" />
      <line x1="16" y1="9" x2="22" y2="15" />
    </svg>
  );
}

/* ---------------- Primary navigation ---------------- */

/** Overview — dashboard grid. */
export function OverviewIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Overview')}>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  );
}

/** Activity — pulse line. */
export function ActivityIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Activity')}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

/** Discover — compass. */
export function DiscoverIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Discover')}>
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );
}

/** Analyze — bar chart. */
export function AnalyzeIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Analyze')}>
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  );
}

/** Saved — bookmark. */
export function SavedIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Saved')}>
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

/** Pipeline — kanban columns. */
export function PipelineIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Pipeline')}>
      <rect x="3" y="3" width="5" height="18" rx="1" />
      <rect x="10" y="3" width="5" height="12" rx="1" />
      <rect x="17" y="3" width="4" height="8" rx="1" />
    </svg>
  );
}

/** Proposals — pencil. */
export function ProposalsIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Proposals')}>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

/** Awards — trophy. */
export function AwardsIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Awards')}>
      <path d="M8 21h8" />
      <path d="M12 17v4" />
      <path d="M7 4h10v6a5 5 0 0 1-10 0V4z" />
      <path d="M7 6H4a1 1 0 0 0-1 1c0 2.2 1.8 4 4 4" />
      <path d="M17 6h3a1 1 0 0 1 1 1c0 2.2-1.8 4-4 4" />
    </svg>
  );
}

/** Readiness — clipboard with a check. */
export function ReadinessIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Readiness')}>
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  );
}

/** GovCon Readiness — shield with a check. */
export function ShieldCheckIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Shield')}>
      <path d="M12 22s8-3.6 8-10V5l-8-3-8 3v7c0 6.4 8 10 8 10z" />
      <path d="m9 11.5 2 2 4-4" />
    </svg>
  );
}

/** Calendar. */
export function CalendarIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Calendar')}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

/* ---------------- Manage navigation ---------------- */

/** Team / Stakeholders — people. */
export function TeamIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Team')}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/** Relationships — heart. */
export function HeartIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Relationships')}>
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </svg>
  );
}

/** Organization / Workspace Setup — building. */
export function BuildingIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Organization')}>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" />
      <path d="M16 6h.01" />
      <path d="M12 6h.01" />
      <path d="M8 10h.01" />
      <path d="M16 10h.01" />
      <path d="M12 10h.01" />
      <path d="M8 14h.01" />
      <path d="M16 14h.01" />
      <path d="M12 14h.01" />
    </svg>
  );
}

/** Vault — lock. */
export function VaultIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Vault')}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** Registrations — ID badge. */
export function RegistrationsIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Registrations')}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <circle cx="8" cy="11" r="2" />
      <path d="M5.5 16.5c.6-1.4 1.4-2 2.5-2s1.9.6 2.5 2" />
      <path d="M14.5 9.5H19" />
      <path d="M14.5 13.5H19" />
    </svg>
  );
}

/** Reports — pie chart. */
export function ReportsIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Reports')}>
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  );
}

/** Tasks — list with checks. */
export function TasksIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Tasks')}>
      <path d="m3 6 2 2 4-4" />
      <path d="M13 6h8" />
      <path d="m3 13 2 2 4-4" />
      <path d="M13 13h8" />
      <path d="m3 20 2 2 4-4" />
      <path d="M13 20h8" />
    </svg>
  );
}

/** Documents — file with text lines. */
export function DocumentsIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Documents')}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

/** Portfolio — briefcase. */
export function PortfolioIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Portfolio')}>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}

/** How-to Guides — open book. */
export function BookOpenIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Guide')}>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

/* ---------------- Individual navigation ---------------- */

/** Home — house. */
export function HomeIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Home')}>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

/** My Applications — clipboard with list lines. */
export function ApplicationsIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Applications')}>
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M9 12h6" />
      <path d="M9 16h4" />
    </svg>
  );
}

/** My Profile — person in a circle. */
export function ProfileIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Profile')}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="10" r="3" />
      <path d="M6.5 19a6 6 0 0 1 11 0" />
    </svg>
  );
}

/* ---------------- Generic UI ---------------- */

/** Search — magnifier. */
export function SearchIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Search')}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/** Notifications — bell. */
export function BellIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Notifications')}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

/** Settings — gear. */
export function SettingsIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Settings')}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3" />
      <path d="M12 19v3" />
      <path d="M2 12h3" />
      <path d="M19 12h3" />
      <path d="m4.9 4.9 2.1 2.1" />
      <path d="m17 17 2.1 2.1" />
      <path d="M19.1 4.9 17 7" />
      <path d="m7 17-2.1 2.1" />
    </svg>
  );
}

/** Log out — door with arrow. */
export function LogoutIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Log out')}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

/** Chevron down. */
export function ChevronDownIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Chevron down')}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/** Chevron left. */
export function ChevronLeftIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Chevron left')}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/** Chevron right. */
export function ChevronRightIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Chevron right')}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/** Menu — hamburger. */
export function MenuIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Menu')}>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

/** Plus. */
export function PlusIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Add')}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

/** Play — triangle. */
export function PlayIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Play')}>
      <polygon points="6 3 20 12 6 21 6 3" />
    </svg>
  );
}

/** Pause — two bars. */
export function PauseIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Pause')}>
      <rect x="5" y="4" width="4" height="16" rx="1" />
      <rect x="15" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

/** Help — question mark in a circle. */
export function HelpCircleIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Help')}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/** Close — X. */
export function XIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Close')}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/** Download — arrow down into a tray. */
export function DownloadIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Download')}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/** Upload — arrow up out of a tray. */
export function UploadIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Upload')}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

/** Check. */
export function CheckIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Check')}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* ---------------- Marketing & onboarding ---------------- */

/** Lightbulb — an idea, explained plainly. */
export function LightbulbIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Idea')}>
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1.3.5 2.6 1.5 3.5.8.8 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" />
      <path d="M10 22h4" />
    </svg>
  );
}

/** Landmark — government / institutions. */
export function LandmarkIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Landmark')}>
      <line x1="3" y1="22" x2="21" y2="22" />
      <line x1="6" y1="18" x2="6" y2="11" />
      <line x1="10" y1="18" x2="10" y2="11" />
      <line x1="14" y1="18" x2="14" y2="11" />
      <line x1="18" y1="18" x2="18" y2="11" />
      <polygon points="12 2 20 7 4 7" />
    </svg>
  );
}

/** Target — a goal to win. */
export function TargetIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Target')}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

/** Unlock — open padlock. */
export function UnlockIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Unlocked')}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </svg>
  );
}

/** Folder — organized files. */
export function FolderIcon({ size = 18, className }: IconProps) {
  return (
    <svg {...base(size, className, 'Folder')}>
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  );
}
